import {
    Button,
    Flex,
    Show,
    Text,
    IconButton,
    useMediaQuery,
    Box
} from '@chakra-ui/react'
import { fetcher, Method } from '@olagg/api-hooks'
import GameCard from '../Cards/GameCard'
import { Link } from 'react-router-dom'
import { useState, useEffect, useCallback, useRef } from 'react'
import type { Game } from '@olagg/db-types'
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next'

export default function GamesCarousel({
    gameEndpoint = { method: Method.GET, path: '' },
    carouselGames = [],
    title = '',
    url = '',
    cardWidth = '250px',
    cardHeight = '300px',
    imgHeight = '233px',
    showRating = false,
    isSameRoute = false,
    handleFuncOnClick = () => { }
}) {
    const navigate = useNavigate();
    const [games, setGames] = useState<Game[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [pageCount, setPageCount] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const { t } = useTranslation()
    const ref = useRef(null);
    const [isLargerThan800] = useMediaQuery('(min-width: 800px)')

    const initRequest = useCallback(async () => {
        if (carouselGames.length == 0 && gameEndpoint.path != '') {
            await fetcher(
                gameEndpoint
            ).then((response: { games: Game[] }) => {
                setGames(response.games)
                setIsLoading(false)
            })
        } else {
            setGames(carouselGames)
        }
        setIsLoading(false)
    }, [])

    useEffect(() => {
        setIsLoading(true)
        initRequest();
    }, [])

    const getPages = () => {
        if (isLargerThan800) {
            let items = games.length
            let pages = Math.ceil(items / 4)
            setPageCount(pages)
        }
    }

    useEffect(() => {
        getPages()
    }, [isLoading])

    const slideLeft = () => {
        let w = ref.current.offsetWidth
        ref.current.scrollTo({
            left: ref.current.scrollLeft - w,
            top: 0,
            behavior: 'smooth'
        });

        if (currentPage > 1) setCurrentPage(currentPage - 1)
    }

    const slideRight = () => {
        let w = ref.current.offsetWidth
        ref.current.scrollTo({
            left: ref.current.scrollLeft + w,
            top: 0,
            behavior: 'smooth'
        });

        if (currentPage < pageCount) setCurrentPage(currentPage + 1)
    }

    const goToUrl = (route) => {
        handleFuncOnClick()
        setTimeout(() => {
            navigate(route);
        }, 1000)
    }

    return (
        <Flex
            id="GamesCarousel"
            flexDir="column"
            w="full"
            position='relative'
        >
            {title && (
                <Flex
                    w="full"
                    justify="space-between"
                    color="white"
                    fontWeight="600"
                    px={{ base: '5vw', lg: '1vw' }}
                    position="relative"
                    zIndex={3}
                    alignItems='center'
                >
                    <Text fontSize='36px' fontWeight='bold'>{title}</Text>
                    {url && (
                        <Link to={url}>
                            <Button variant="link" color="white" fontSize="16px">
                                {t('Dashboard.games.viewMore')}
                            </Button>
                        </Link>
                    )}
                </Flex>
            )}

            {!isLoading && (
                <Show above='lg'>
                    {
                        currentPage > 1 && (
                            <IconButton
                                bg='white'
                                size='lg'
                                rounded='50%'
                                aria-label='Anterior'
                                icon={<FaChevronLeft />}
                                left='7px'
                                onClick={() => {
                                    slideLeft()
                                }}
                                position='absolute'
                                zIndex={2}
                                top="calc(50% - 24px)"
                                boxShadow='1px 1px 3px rgba(0,0,0,0.5)'
                            />
                        )
                    }
                    {
                        currentPage < pageCount && (
                            <IconButton
                                bg='white'
                                size='lg'
                                rounded='50%'
                                aria-label='Siguiente'
                                icon={<FaChevronRight />}
                                right='7px'
                                marginLeft='auto'
                                onClick={() => {
                                    slideRight()
                                }}
                                position='absolute'
                                zIndex={2}
                                top="calc(50% - 24px)"
                                boxShadow='-1px 1px 3px rgba(0,0,0,0.5)'
                            />
                        )
                    }
                </Show>
            )}

            <Flex
                id="game-featured-slider"
                scrollSnapType="x mandatory"
                overflowX='scroll'
                py={8}
                css={{
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    }
                }}
                ref={ref}
            >
                {games?.map((g, index) => (
                    !isSameRoute ? (
                        <Link to={`/juego/${g.slug}`} key={index}>
                            <Flex scrollSnapAlign="start">
                                <GameCard
                                    game={g}
                                    width={cardWidth}
                                    height={cardHeight}
                                    bgColor='white'
                                    imageH={imgHeight}
                                    showRating={showRating}
                                />
                            </Flex>
                        </Link>
                    ) : (
                        <Box cursor='pointer' onClick={() => {
                            goToUrl(`/juego/${g.slug}`)
                        }} key={index}>
                            <Flex scrollSnapAlign="start">
                                <GameCard
                                    game={g}
                                    width={cardWidth}
                                    height={cardHeight}
                                    bgColor='white'
                                    imageH={imgHeight}
                                    showRating={showRating}
                                />
                            </Flex>
                        </Box>
                    )
                ))}
            </Flex>
        </Flex>
    )
}
